import React, { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import axios from "../utils/axiosInstance";

const SOCKET_URL = process.env.REACT_APP_API_URL || (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");

// Short base64 chime — kept inline so the bell never depends on a network asset.
const CHIME_SRC = "data:audio/wav;base64,UklGRlwGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YTgGAACAgICBgoOEhYaHiImKi4yNjo+QkZKTlJWWl5iZmpucnZ6foKGio6SlpqeoqaqrrK2ur7CxsrO0tba3uLm6u7y9vr/AwcLDxMXGx8jJysvMzc7P0NHS09TV1tfY2drb3N3e3+Dh4uPk5ebn6Onq6+zt7u/w8fLz9PX29/j5+vv8/f7/AAECAwQFBgcICQoLDA0ODxAREhMUFRYXGBkaGxwdHh8gISIjJCUmJygpKissLS4vMDEyMzQ1Njc4OTo7PD0+P0BBQkNERUZHSElKS0xNTk9QUVJTVFVWV1hZWltcXV5fYGFiY2RlZmdoaWprbG1ub3BxcnN0dXZ3eHl6e3x9fn+AgYKDhIWGh4iJiouMjY6PkJGSk5SVlpeYmZqbnJ2en6ChoqOkpaanqKmqq6ytrq+wsbKztLW2t7i5uru8vb6/wMHCw8TFxsfIycrLzM3Oz9DR0tPU1dbX2Nna29zd3t/g4eLj5OXm5+jp6uvs7e7v8PHy8/T19vf4+fr7/P3+/wABAgMEBQYHCAkKCwwNDg8QERITFBUWFxgZGhscHR4fICEiIyQlJicoKSorLC0uLzAxMjM0NTY3ODk6Ozw9Pj9AQUJDREVGR0hJSktMTU5PUFFSU1RVVldYWVpbXF1eX2BhYmNkZWZnaGlqa2xtbm9wcXJzdHV2d3h5ent8fX5/gIGCg4SFhoeIiYqLjI2Oj5CRkpOUlZaXmJmam5ydnp+goaKjpKWmp6ipqqusra6vsLGys7S1tre4ubq7vL2+v8DBwsPExcbHyMnKy8zNzs/Q0dLT1NXW19jZ2tvc3d7f4OHi4+Tl5ufo6err7O3u7/Dx8vP09fb3+Pn6+/z9/v8AAQIDBAUGBwgJCgsMDQ4PEBESExQVFhcYGRobHB0eHyAhIiMkJSYnKCkqKywtLi8wMTIzNDU2Nzg5Ojs8PT4/QEFCQ0RFRkdISUpLTE1OT1BRUlNUVVZXWFlaW1xdXl9gYWJjZGVmZ2hpamtsbW5vcHFyc3R1dnd4eXp7fH1+f4CBgoOEhYaHiImKi4yNjo+QkZKTlJWWl5iZmpucnZ6foKGio6SlpqeoqaqrrK2ur7CxsrO0tba3uLm6u7y9vr/AwcLDxMXGx8jJysvMzc7P0NHS09TV1tfY2drb3N3e3+Dh4uPk5ebn6Onq6+zt7u/w8fLz9PX29/j5+vv8/f7/";

export default function ImicoNotificationBell({ onOpenCertificate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const audioRef = useRef(null);
  const unlockedRef = useRef(false);
  const seenIdsRef = useRef(new Set());

  const playChime = useCallback(() => {
    try {
      if (!audioRef.current) audioRef.current = new Audio(CHIME_SRC);
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(() => {});
    } catch { /* autoplay blocked until user interacts once */ }
  }, []);

  useEffect(() => {
    const unlock = () => { unlockedRef.current = true; };
    window.addEventListener("click", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get("/api/imico/notifications");
      const rows = res.data || [];
      const newOnes = rows.filter(r => !r.is_read && !seenIdsRef.current.has(r.notification_id));
      rows.forEach(r => seenIdsRef.current.add(r.notification_id));
      if (newOnes.length > 0 && unlockedRef.current) playChime();
      setItems(rows);
      setCount(rows.filter(r => !r.is_read).length);
    } catch { /* silent — bell degrades gracefully */ }
  }, [playChime]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 20000);
    const token = localStorage.getItem("token");
    const socket = io(SOCKET_URL, { auth: { token }, transports: ["websocket", "polling"] });
    socket.on("imico-certificate-updated", () => fetchNotifications());
    return () => { clearInterval(interval); socket.disconnect(); };
  }, [fetchNotifications]);

  const handleOpenItem = async (item) => {
    try {
      await axios.put(`/api/imico/notifications/${item.notification_id}/read`);
    } catch { /* ignore */ }
    setOpen(false);
    fetchNotifications();
    if (onOpenCertificate && item.reference_id) onOpenCertificate(item.reference_id);
  };

  const markAllRead = async () => {
    try {
      await axios.put("/api/imico/notifications/read-all");
      fetchNotifications();
    } catch { /* ignore */ }
  };

  return (
    <div className="imico-bell-wrap">
      <button className="imico-bell-btn" onClick={() => setOpen(o => !o)} aria-label="Notifications">
        <i className="bi bi-bell-fill" />
        {count > 0 && <span className="imico-bell-badge">{count > 99 ? "99+" : count}</span>}
      </button>
      {open && (
        <>
          <div className="imico-bell-backdrop" onClick={() => setOpen(false)} />
          <div className="imico-bell-dropdown">
            <div className="imico-bell-header">
              <span>Amatangazo</span>
              {count > 0 && <button onClick={markAllRead} className="imico-bell-markall">Byose yasomwe</button>}
            </div>
            <div className="imico-bell-list">
              {items.length === 0 && <div className="imico-bell-empty">Nta matangazo</div>}
              {items.map(item => (
                <button
                  key={item.notification_id}
                  className={`imico-bell-item${item.is_read ? "" : " unread"}`}
                  onClick={() => handleOpenItem(item)}
                >
                  <div className="imico-bell-item-title">{item.title}</div>
                  <div className="imico-bell-item-content">{item.content}</div>
                  <div className="imico-bell-item-time">{new Date(item.created_at).toLocaleString("rw-RW")}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
